import Events from "../utils/Events.js";
import {daysBetween} from "../utils/General.js";
import uuidv4 from "uuid/v4";

/**
* NuCalController.
* @param {Object} props properties.
* @param {String} props.namespace The namespace in which this object should subscribe and publish events on.
* @param {Number} props.minNumberOfNights The minimum number of nights between the arrival and departure date. (Integer)
* @param {Number} props.maxNumberOfNights The maximum number of nights between the arrival and departure date. (Integer) 
* @param {Date} props.minDate The minimum date for the arrival date. 
* @param {Date} props.maxDate The maximum date for the departure date.
* @param {Date} props.arrivalDate The date of arrival. This must be before the departure date.
* @param {Date} props.departureDate The date of arrival. This must be after the arrival date.
*/

class NuCalController {

	constructor (props = {}) {
		this.namespace = props.namespace || uuidv4();
		this.minNumberOfNights = props.minNumberOfNights || 1;
		this.maxNumberOfNights = props.maxNumberOfNights || 365;
		this.minDate = props.minDate || new Date();
		this.maxDate = props.maxDate || function() { const d = new Date(); d.setFullYear(d.getFullYear() + 2); return d; }();
		this.arrivalDate = props.arrivalDate || new Date();
		this.departureDate = props.departureDate || new Date();
		this.constructed = true;
		this._rationalizeDateRange({});
	}

	/**
     * The namespace in which this object should subscribe and publish events on.
     * @type {String}.
    */
	get namespace(){
		return this._namespace;
	}

	set namespace(n){
		this._namespace = n;
		if(typeof n == "string" && this._namespace !== ""){
			n = "/" + n;
		}else{
			n = "";
		}
		this._events = {};
		//sending
		this._events.datesUpdated = `/nuCal${n}/datesUpdated`;
		this._events.arrivalDateUpdated = `/nuCal${n}/arrivalDateUpdated`;
		this._events.departureDateUpdated = `/nuCal${n}/departureDateUpdated`;
		this._events.minDateUpdated = `/nuCal${n}/minDateUpdated`;
		this._events.maxDateUpdated = `/nuCal${n}/maxDateUpdated`;
		this._events.numberOfNightsUpdated = `/nuCal${n}/numberOfNightsUpdated`;
		this._events.minNumberOfNightsUpdated = `/nuCal${n}/minNumberOfNightsUpdated`;
		this._events.maxNumberOfNightsUpdated = `/nuCal${n}/maxNumberOfNightsUpdated`;
		//receiving
		this._events.updateArrivalDate = `/nuCal${n}/updateArrivalDate`;
		this._events.updateDepartureDate = `/nuCal${n}/updateDepartureDate`;
		this._events.updateMinDate = `/nuCal${n}/updateMinDate`;
		this._events.updateMaxDate = `/nuCal${n}/updateMaxDate`;
		this._events.updateNumberOfNights = `/nuCal${n}/updateNumberOfNights`;
		this._events.updateMinNumberOfNights = `/nuCal${n}/updateMinNumberOfNights`;
		this._events.updateMaxNumberOfNights = `/nuCal${n}/updateMaxNumberOfNights`;

		Events.subscribe(this._events.updateArrivalDate, (props) => {
			this.arrivalDate = props.date;
		});
		Events.subscribe(this._events.updateDepartureDate, (props) => {
			this.departureDate = props.date;
		});
		Events.subscribe(this._events.updateMinDate, (props) => {
			this.minDate = props.date;
		});
		Events.subscribe(this._events.updateMaxDate, (props) => {
			this.maxDate = props.date;
		});
		Events.subscribe(this._events.updateMinNumberOfNights, (props) => {
			this.minNumberOfNights = props.minNumberOfNights;
		});
		Events.subscribe(this._events.updateMaxNumberOfNights, (props) => {
			this.maxNumberOfNights = props.maxNumberOfNights;
		});
		Events.subscribe(this._events.updateNumberOfNights, (props) => {
			this.numberOfNights = props.numberOfNights;
		});
	}

	/**
     * The minimum date for the arrival date.
     * @type {Date}.
    */
	get minDate(){
		return this._minDate;
	}

	set minDate(nuMinDate){
		if(nuMinDate instanceof Date && nuMinDate.toString() !== "Invalid Date"){
			this._minDate = new Date(nuMinDate.getFullYear(),nuMinDate.getMonth(),nuMinDate.getDate());
		}else{
			return false;
		}
		const changes = {};
		changes.minDate = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The maximum date for the departure date.
     * @type {Date}.
    */
	get maxDate(){
		return this._maxDate;
	}

	set maxDate(nuMaxDate){
		if(nuMaxDate instanceof Date && nuMaxDate.toString() !== "Invalid Date"){
			this._maxDate = new Date(nuMaxDate.getFullYear(),nuMaxDate.getMonth(),nuMaxDate.getDate());
		}else{
			return false;
		}
		const changes = {};
		changes.maxDate = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The number of nights between the arrival and departure date.
     * When this is explicitly set then it becomes fixed and the departure
     * date will adjust as the arrival date changes. If the departure date is
     * explicitly set then this property becomes dynamic and reflex the difference
     * between the arrival and departure date. (Integer)
     * @type {Number}.
    */
	get numberOfNights(){
		return daysBetween(this._arrivalDate,this._departureDate);
	}

	set numberOfNights(value){
		if(value >= this.minNumberOfNights){
			if(this.maxNumberOfNights !== null && value > this.maxNumberOfNights){
				return;
			}
			this._fixedNumberOfNights = parseInt(value);
			const changes = {};
			changes.numberOfNights = true;
			this._rationalizeDateRange(changes);
		}
	}

	/**
     * The minimun number of nights between the arrival and departure date. (Integer)
     * @type {Number}.
    */
	get minNumberOfNights(){
		return parseInt(this._minNumberOfNights);
	}

	set minNumberOfNights(nuMinNo){
		if(typeof nuMinNo === "number" && nuMinNo >= 1){
			this._minNumberOfNights = nuMinNo;
		}else{
			if(typeof this._minNumberOfNights === "undefined")
				this._minNumberOfNights = 1;
		}
		const changes = {};
		changes.minNumberOfNights = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The maximum number of nights between the arrival and departure date.
     * @type {Number}.
    */
	get maxNumberOfNights(){
		return parseInt(this._maxNumberOfNights) || null;
	}

	set maxNumberOfNights(nuMaxNo){
		if(typeof nuMaxNo === "number" || nuMaxNo === null){
			this._maxNumberOfNights = nuMaxNo;
		}else{
			if(typeof this._maxNumberOfNights === "undefined")
				this._maxNumberOfNights = null;
		}
		const changes = {};
		changes.maxNumberOfNights = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The date of arrival. This must be before the departure date.
     * @type {Date}
    */
	get arrivalDate(){
		return this._arrivalDate;
	}

	set arrivalDate(nuArrivalDate){
		if(nuArrivalDate instanceof Date && nuArrivalDate.toString() !== "Invalid Date"){
			this._arrivalDate = new Date(nuArrivalDate.getFullYear(),nuArrivalDate.getMonth(),nuArrivalDate.getDate());
		}
		const changes = {};
		changes.arrivalDate = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The date of departure. This must be before the departure date.
     * @type {Date}
    */
	get departureDate(){
		return this._departureDate;
	}

	set departureDate(nuDepartureDate){
		if(nuDepartureDate instanceof Date && nuDepartureDate.toString() !== "Invalid Date"){
			this._fixedNumberOfNights = null;
			this._departureDate = new Date(nuDepartureDate.getFullYear(),nuDepartureDate.getMonth(),nuDepartureDate.getDate());
		}
		const changes = {};
		changes.departureDate = true;
		this._rationalizeDateRange(changes);
	}

	/**
     * The minimum possible arrival date given the current settings. (read-only)
     * @type {Date}
     * @readonly
    */
	get minArrivalDate(){
		return new Date(this.minDate.getFullYear(),this.minDate.getMonth(),this.minDate.getDate());
	}

	/**
     * The minimum possible departure date given the current settings. (read-only)
     * @type {Date}
     * @readonly
    */
	get minDepartureDate(){
		return new Date(this.arrivalDate.getFullYear(),this.arrivalDate.getMonth(),this.arrivalDate.getDate() + this.minNumberOfNights);
	}

	/**
     * The maximum possible arrival date given the current settings. (read-only)
     * @type {Date}
     * @readonly
    */
	get maxArrivalDate(){
		return new Date(this.maxDate.getFullYear(),this.maxDate.getMonth(),this.maxDate.getDate() - this.minNumberOfNights);
	}

	/**
     * The maximum possible departure date given the current settings. (read-only)
     * @type {Date}
     * @readonly
    */
	get maxDepartureDate(){
		if(this.maxNumberOfNights === null)
			return new Date(this.maxDate.getFullYear(),this.maxDate.getMonth(),this.maxDate.getDate());
		else
			return new Date(this.arrivalDate.getFullYear(),this.arrivalDate.getMonth(),this.arrivalDate.getDate() + this.maxNumberOfNights );
	}

	/**
     * Private. Enforces logical rules and such as the departure date being before the arrival date.
     * @private
     * @type {Undefined}
    */
	_rationalizeDateRange(changes = {}){
		if(!this.constructed) return;
		if(this.minDate instanceof Date){
			const minArrivalDate = new Date(this.minArrivalDate.valueOf());
			const minDepartureDate = new Date(this.minDepartureDate.valueOf());
			if(+this.arrivalDate <= +minArrivalDate){
				this._arrivalDate = minArrivalDate;
				changes.arrivalDate = true;
			}
			if(+this.departureDate <= +minDepartureDate){
				this._departureDate = minDepartureDate;
				changes.departureDate = true;
			}
		}
		if(this.maxDate instanceof Date){
			const maxArrivalDate = new Date(this.maxArrivalDate.valueOf());
			const maxDepartureDate = new Date(this.maxDepartureDate.valueOf());
			if(+this.arrivalDate >= +maxArrivalDate){
				this._arrivalDate = maxArrivalDate;
				changes.arrivalDate = true;
			}
			if(+this.departureDate >= +maxDepartureDate){
				this._departureDate = maxDepartureDate;
				changes.departureDate = true;

			}
		}
		if(+this.arrivalDate >= +this.departureDate){
			const tomorrow = new Date(this.arrivalDate.getFullYear(),this.arrivalDate.getMonth(),this.arrivalDate.getDate());
			tomorrow.setDate(tomorrow.getDate() + this.minNumberOfNights);
			this._departureDate = tomorrow;
			changes.departureDate = true;
		}
		if(this._fixedNumberOfNights){
			this._departureDate = new Date(this.arrivalDate.getFullYear(),this.arrivalDate.getMonth(),this.arrivalDate.getDate() + this._fixedNumberOfNights);
			if(+this._departureDate > +this.maxDate){
				this._departureDate = new Date(this.maxDate.valueOf());
			}
		}
		this._triggerEvents(changes);
	}

	/**
     * Private. Publishes events for each property that has changed.
     * @private
     * @type {Undefined}
    */
	_triggerEvents(changes){
		if(changes.arrivalDate){
			Events.publish(this._events.arrivalDateUpdated,{ "arrivalDate": new Date(this.arrivalDate.valueOf()) });
		}
		if(changes.departureDate){
			Events.publish(this._events.departureDateUpdated,{ "departureDate": new Date(this.departureDate.valueOf()) });
		}
		if(changes.numberOfNights){
			Events.publish(this._events.numberOfNightsUpdated,{ "departureDate": new Date(this.departureDate.valueOf()) });
		}
		if(changes.maxDate && this.maxDate instanceof Date){
			Events.publish(this._events.maxDateUpdated,{ "maxDate": new Date(this.maxDate.valueOf()) });
		}
		if(changes.minDate && this.minDate instanceof Date){
			Events.publish(this._events.minDateUpdated,{ "minDate": new Date(this.minDate.valueOf()) });
		}
		if(changes.maxNumberOfNights && this.maxNumberOfNights instanceof Number){
			Events.publish(this._events.maxNumberOfNightsUpdated,{ "maxNumberOfNights": this.maxNumberOfNights });
		}
		if(changes.minNumberOfNights && this.minNumberOfNights instanceof Number){
			Events.publish(this._events.minNumberOfNightsUpdated,{ "minNumberOfNights": this.minNumberOfNights });
		}
		if(Object.keys(changes).length > 0){
			const props = {};
			props.arrivalDate = (this.arrivalDate instanceof Date)? new Date(this.arrivalDate.getFullYear(),this.arrivalDate.getMonth(),this.arrivalDate.getDate()) : null;
			props.departureDate = (this.departureDate instanceof Date)? new Date(this.departureDate.getFullYear(),this.departureDate.getMonth(),this.departureDate.getDate()) : null;
			props.minDate = (this.minDate instanceof Date)? new Date(this.minDate.valueOf()) : null;
			props.maxDate = (this.maxDate instanceof Date)? new Date(this.maxDate.valueOf()) : null;
			if(this.minDate instanceof Date){
				props.minArrivalDate = new Date(this.minArrivalDate.valueOf());
				props.minDepartureDate = new Date(this.minDepartureDate.valueOf());
			}
			if(this.maxDate instanceof Date){
				props.maxArrivalDate = new Date(this.maxArrivalDate.valueOf());
				props.maxDepartureDate = new Date(this.maxDepartureDate.valueOf());
			}
			props.minNumberOfNights = this.minNumberOfNights;
			props.maxNumberOfNights = this.maxNumberOfNights;
			if(this.arrivalDate instanceof Date && this.departureDate instanceof Date){
				Events.publish(this._events.datesUpdated,props);
			}
		}
	}


}

export default NuCalController;